import { defineStore } from 'pinia'
import type { AuthState, User } from '../types'
import { userService } from '@/services/userService'
import { REFRESH_TOKEN } from '@/services/apiClient'
import { useSettingsStore } from './settings'

const TOKEN_KEY = 'quran-token'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN),
    isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
    loading: false,
  }),

  getters: {
    /**
     * Get current user
     */
    currentUser: (state): User | null => state.user,

    /**
     * Check if user is authenticated
     */
    authenticated: (state): boolean => state.isAuthenticated && !!state.token,

    /**
     * Get auth token
     */
    authToken: (state): string | null => state.token,
  },

  actions: {
    /**
     * Set authentication token
     */
    setToken(token: string) {
      this.token = token
      this.isAuthenticated = true
      localStorage.setItem(TOKEN_KEY, token)
    },

    /**
     * Set refresh token (used to silently mint a new access token on a 401)
     */
    setRefreshToken(refreshToken: string) {
      this.refreshToken = refreshToken
      localStorage.setItem(REFRESH_TOKEN, refreshToken)
    },

    /**
     * Set current user
     */
    setUser(user: User) {
      this.user = user
      this.isAuthenticated = true
    },

    /**
     * Login action - sets both tokens and user, then hydrates per-user state
     */
    async login(token: string, refreshToken: string, user?: User) {
      this.setToken(token)
      this.setRefreshToken(refreshToken)
      if (user) {
        this.setUser(user)
      }
      await this.syncUserSettings()
    },

    /**
     * Logout action - clears all auth data and resets per-user settings
     */
    logout() {
      this.user = null
      this.token = null
      this.refreshToken = null
      this.isAuthenticated = false
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN)
      useSettingsStore().resetToDefaults()
    },

    /**
     * Fetch current user details
     */
    async fetchCurrentUser() {
      try {
        const user = await userService.getCurrentUser()
        if (user) {
          this.setUser(user)
        }
        return user
      } catch (error) {
        console.error('Error fetching current user:', error)
        return null
      }
    },

    async reloadUser() {
      if (!this.user?.id) return this.fetchCurrentUser()
      try {
        const user = await userService.getUser(this.user.id)
        this.setUser(user)
        return user
      } catch (error) {
        console.error('Error reloading user:', error)
        return null
      }
    },

    /**
     * Check authentication status on app init
     */
    async checkAuth() {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        this.token = token
        this.refreshToken = localStorage.getItem(REFRESH_TOKEN)
        this.isAuthenticated = true
        // Fetch user profile
        await this.fetchCurrentUser()
        await this.syncUserSettings()
      } else {
        this.logout()
      }
    },

    /**
     * Set loading state
     */
    setLoading(loading: boolean) {
      this.loading = loading
    },

    /**
     * Hydrate per-user state after auth. Settings are awaited since the
     * router's riwaya gate reads them right after; the catalog isn't.
     */
    async syncUserSettings() {
      const settingsStore = useSettingsStore()
      await settingsStore.syncFromBackend()
      settingsStore.fetchRiwayat()
    },
  },
})
